/**
 * Re-anchoring spans after the caption text is edited.
 *
 * Mirrors `backend/app/services/textdiff.py`; the two must stay in step, because the
 * server recomputes human-added spans with the same algorithm.
 *
 * The legacy version dropped un-relocatable spans silently. This one reports what
 * moved and what was lost so the UI can offer an undo - the single biggest
 * data-quality improvement in the rewrite.
 */

const TOKEN_RE = /\s+|[A-Za-z0-9]+|[^\sA-Za-z0-9]/g;

export function tokenize(text) {
  return [...String(text || '').matchAll(TOKEN_RE)].map((m) => ({
    text: m[0],
    start: m.index,
    end: m.index + m[0].length,
  }));
}

/** Token-level longest common subsequence, returned as aligned pairs in order. */
export function alignTokens(oldText, newText) {
  const a = tokenize(oldText);
  const b = tokenize(newText);
  if (!a.length || !b.length) return [];

  const table = Array.from({ length: a.length + 1 }, () => new Int32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i][j] =
        a[i].text === b[j].text
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const pairs = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i].text === b[j].text) {
      pairs.push({ oldToken: a[i], newToken: b[j] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return pairs;
}

/** Project an old offset into the new text, interpolating across edited regions. */
function projectOffset(offset, pairs, maxLength) {
  if (!pairs.length) return Math.min(offset, maxLength);

  for (const pair of pairs) {
    if (offset >= pair.oldToken.start && offset <= pair.oldToken.end) {
      const within = Math.min(
        offset - pair.oldToken.start,
        pair.newToken.end - pair.newToken.start,
      );
      return Math.min(maxLength, pair.newToken.start + Math.max(0, within));
    }
  }

  const before = [...pairs].reverse().find((p) => p.oldToken.end <= offset);
  const after = pairs.find((p) => p.oldToken.start >= offset);
  if (before && after) {
    const gap = Math.max(1, after.oldToken.start - before.oldToken.end);
    const t = Math.min(1, Math.max(0, (offset - before.oldToken.end) / gap));
    return Math.round(before.newToken.end + t * (after.newToken.start - before.newToken.end));
  }
  if (before) return before.newToken.end;
  if (after) return after.newToken.start;
  return Math.min(offset, maxLength);
}

/** Nearest literal occurrence of `needle` to a projected position. */
function nearestOccurrence(haystack, needle, near) {
  const text = String(needle || '');
  if (!text.trim()) return null;
  const lowerHay = haystack.toLowerCase();
  const lowerNeedle = text.toLowerCase();

  const hits = [];
  for (let k = lowerHay.indexOf(lowerNeedle); k >= 0; k = lowerHay.indexOf(lowerNeedle, k + 1)) {
    hits.push({ start: k, end: k + text.length });
  }
  if (!hits.length) return null;
  return hits.sort((x, y) => Math.abs(x.start - near) - Math.abs(y.start - near))[0];
}

/**
 * Move a list of spans onto edited text.
 * @returns {{spans: Array, moved: Array, dropped: Array}}
 */
export function remapSpans(spans, oldText, newText, pairs = null) {
  const aligned = pairs ?? alignTokens(oldText, newText);
  const kept = [];
  const moved = [];
  const dropped = [];

  for (const span of spans || []) {
    const original = span.text || oldText.slice(span.start_char, span.end_char);
    const guess = projectOffset(span.start_char, aligned, newText.length);
    const hit = nearestOccurrence(newText, original, guess);

    const rawStart = hit ? hit.start : guess;
    const rawEnd = hit ? hit.end : projectOffset(span.end_char, aligned, newText.length);

    const start = Math.max(0, Math.min(newText.length, rawStart));
    const end = Math.max(start, Math.min(newText.length, rawEnd));
    const text = newText.slice(start, end);

    if (!text.trim()) {
      dropped.push(span);
      continue;
    }
    const next = { ...span, start_char: start, end_char: end, text };
    kept.push(next);
    if (start !== span.start_char || end !== span.end_char) moved.push(next);
  }

  // Re-anchoring can push two spans into each other; the server rejects overlaps, so
  // resolve here by dropping the later one rather than failing the save.
  kept.sort((a, b) => a.start_char - b.start_char || a.end_char - b.end_char);
  const final = [];
  for (const span of kept) {
    const previous = final[final.length - 1];
    if (previous && span.start_char < previous.end_char) {
      dropped.push(span);
      continue;
    }
    final.push(span);
  }

  return { spans: final, moved, dropped };
}

const FILLERS = new Set([
  'a', 'an', 'and', 'or', 'the', 'of', 'to', 'in', 'on', 'with',
  'at', 'by', 'for', 'from', 'as', 'that', 'is', 'are',
]);

/** Contiguous runs of new text with no counterpart in the old text. */
export function addedRanges(oldText, newText) {
  const pairs = alignTokens(oldText, newText);
  const matched = new Set(pairs.map((p) => p.newToken.start));
  const tokens = tokenize(newText);

  const runs = [];
  let i = 0;
  while (i < tokens.length) {
    if (matched.has(tokens[i].start)) {
      i += 1;
      continue;
    }
    const startIndex = i;
    while (i < tokens.length && !matched.has(tokens[i].start)) i += 1;
    runs.push({ start: tokens[startIndex].start, end: tokens[i - 1].end });
  }
  return runs;
}

/** Bridge runs separated only by short filler, so one edit reads as one highlight. */
export function mergeNearbyRuns(text, runs, maxGap = 32) {
  const sorted = [...runs].sort((a, b) => a.start - b.start || a.end - b.end);
  const out = [];
  for (const run of sorted) {
    const previous = out[out.length - 1];
    if (!previous) {
      out.push({ ...run });
      continue;
    }
    const gap = String(text).slice(previous.end, run.start);
    const words = gap.toLowerCase().match(/[a-z0-9]+/g) || [];
    const bridgeable =
      gap.length <= maxGap && words.every((w) => FILLERS.has(w)) && !/[.!?]\s|\n{2,}/.test(gap);
    if (bridgeable) previous.end = Math.max(previous.end, run.end);
    else out.push({ ...run });
  }
  return out;
}

/**
 * Text the human typed that the agent did not write.
 * The server recomputes this on save; the client version exists only to show the
 * purple highlight immediately.
 */
export function humanAddedSpans(baseText, currentText, exclude = []) {
  const base = String(baseText || '');
  const current = String(currentText || '');
  if (!current) return [];
  if (!base) {
    return [
      { start_char: 0, end_char: current.length, text: current, source: 'direct_human_caption_edit' },
    ];
  }

  const overlaps = (run) =>
    exclude.some((e) => run.start < e.end_char && e.start_char < run.end);

  const runs = addedRanges(base, current).filter((r) => !overlaps(r));

  return mergeNearbyRuns(current, runs)
    .map((run) => ({
      start_char: run.start,
      end_char: run.end,
      text: current.slice(run.start, run.end),
      source: 'direct_human_caption_edit',
    }))
    .filter((span) => span.text.trim());
}
